import type { ActionPlan } from '../lib/actions';
import { isEmptyPlan } from '../lib/actions';
import { externalHref } from '../lib/url';

/**
 * The to-do list for a domain, on screen and in the PDF.
 *
 * Ordered by what pays back first: pages AI already reads (an edit shows on the
 * next run), then how the site is built, then off-site work (weeks). Every item
 * says why it is there, because "change this page" without the reason is an
 * instruction nobody can check.
 */

interface Palette { ink: string; muted: string; rule: string; accent: string; good: string; bad: string; warn: string }

const SCREEN: Palette = {
  ink: '#FFFFFF', muted: '#A0A0AC', rule: '#2A2A33',
  accent: '#00FFD4', good: '#6BE07A', bad: '#E0574A', warn: '#E0B341',
};

export default function ActionPlanView({ plans, print, palette }: {
  plans: ActionPlan[];
  /** PDF mode: smaller type, and the URLs printed in full because nobody can click paper. */
  print?: boolean;
  palette?: Palette;
}): JSX.Element | null {
  if (plans.length === 0) return null;
  const c = palette ?? SCREEN;
  const t = print
    ? { h: 'text-[10.5px] font-semibold', p: 'text-[9px] leading-relaxed', li: 'text-[9.5px] leading-relaxed', n: 'text-[9px]' }
    : { h: 'text-base font-semibold', p: 'text-sm leading-relaxed', li: 'text-sm leading-relaxed', n: 'text-xs' };

  const Step = ({ n, title, why, children }: { n: string; title: string; why: string; children: React.ReactNode }): JSX.Element => (
    <div className={`${print ? 'aivs-keep mb-3' : 'mb-5'}`}>
      <div className="flex items-baseline gap-2">
        <span className={`font-mono ${t.n}`} style={{ color: c.accent }}>{n}</span>
        <p className={t.h} style={{ color: c.ink }}>{title}</p>
      </div>
      <p className={`${t.p} mt-0.5 mb-1.5`} style={{ color: c.muted }}><strong style={{ color: c.ink }}>Why: </strong>{why}</p>
      {children}
    </div>
  );

  const Where = ({ where }: { where: string[] }): JSX.Element => (
    <span style={{ color: c.muted }}> · {where.join(', ')}</span>
  );

  return (
    <div>
      {plans.map((p) => {
        let n = 0;
        const next = (): string => String(++n);
        return (
          <div key={p.domain} className={plans.length > 1 ? 'mb-4' : ''}>
            {plans.length > 1 && <p className={`font-mono ${t.n} uppercase tracking-[0.14em] mb-2`} style={{ color: c.accent }}>{p.domain}</p>}

            {isEmptyPlan(p) && (
              <p className={t.p} style={{ color: c.muted }}>
                Nothing to change from these runs{p.named ? `: AI named you on ${p.named} ${p.named === 1 ? 'question' : 'questions'} and lost none it could have given you` : ''}.
              </p>
            )}

            {p.pages.length > 0 && (
              <Step n={next()} title={`Edit ${p.pages.length} ${p.pages.length === 1 ? 'page' : 'pages'} AI already reads`}
                why="AI used these pages as a source for the questions below, then named a competitor instead of you. The page is right, the brand did not survive the paraphrase. Put your name in the sentence that answers the question, near the top of the page. It is the cheapest fix here and the next run shows whether it worked.">
                <ul className={t.li}>
                  {p.pages.map((pg) => (
                    <li key={pg.url} className="py-1" style={{ borderBottom: `1px solid ${c.rule}` }}>
                      <a href={externalHref(pg.url)} target="_blank" rel="noreferrer" className="break-all hover:underline" style={{ color: c.accent }}>
                        {pg.url}{!print && ' ↗'}
                      </a>
                      <ul className="mt-0.5 pl-3">
                        {pg.questions.map((q) => (
                          <li key={q.query} style={{ color: c.ink }}>
                            “{q.query}”<Where where={q.where} />
                            {q.rivals.length > 0 && <span style={{ color: c.muted }}>, named instead: <span style={{ color: c.bad }}>{q.rivals.slice(0, 4).join(', ')}</span></span>}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </Step>
            )}

            {p.siteOnly.length > 0 && (
              <Step n={next()} title={`Answer ${p.siteOnly.length} ${p.siteOnly.length === 1 ? 'question' : 'questions'} on the page that covers it`}
                why={`AI used ${p.domain} as a source here and named a competitor, but the engine reports only the site, not the page (Gemini does this). Find the page on your site that answers the question, and make its first sentence answer it with your name in it. If no page answers it, that page is what is missing.`}>
                <ul className={t.li}>
                  {p.siteOnly.map((q) => (
                    <li key={q.query} className="py-0.5" style={{ borderBottom: `1px solid ${c.rule}`, color: c.ink }}>
                      “{q.query}”<Where where={q.where} />
                      {q.rivals.length > 0 && <span style={{ color: c.muted }}>, named instead: <span style={{ color: c.bad }}>{q.rivals.slice(0, 4).join(', ')}</span></span>}
                    </li>
                  ))}
                </ul>
              </Step>
            )}

            {p.build.length > 0 && (
              <Step n={next()} title={`Fix ${p.build.length} ${p.build.length === 1 ? 'thing' : 'things'} in how the site is built`}
                why="These are measured on your page, not sampled from AI. They decide whether AI can read the page and tie it to your name at all, and they show up in readiness as soon as you rescan.">
                <ul className={t.li}>
                  {p.build.map((f) => (
                    <li key={f.title} className="py-1" style={{ borderBottom: `1px solid ${c.rule}` }}>
                      <span style={{ color: f.severity === 'critical' ? c.bad : c.warn }}>{f.severity === 'critical' ? 'Critical' : 'Worth fixing'}</span>
                      <span style={{ color: c.ink }}> · {f.title}</span>
                      <div style={{ color: c.ink }}><strong>Change: </strong>{f.fix}</div>
                      {f.evidence && <div style={{ color: c.muted }}><strong>Found: </strong>{f.evidence}</div>}
                    </li>
                  ))}
                </ul>
              </Step>
            )}

            {p.offSite.length > 0 && (
              <Step n={next()} title={`Get mentioned where AI looks, for ${p.offSite.length} ${p.offSite.length === 1 ? 'question' : 'questions'}`}
                why="On these questions AI never reached your site, on any engine in range. No edit on your own pages changes that. It answers from other sites, so the work is being named on them: listings, comparisons, partner pages, reviews. This takes weeks to show, because those pages have to be crawled first.">
                {p.targets.length > 0 && (
                  <p className={`${t.p} mb-1`} style={{ color: c.ink }}>
                    <strong>Start with the sites AI cited instead: </strong>
                    {p.targets.map((x) => `${x.host} (${x.queries}×)`).join(', ')}
                  </p>
                )}
                <ul className={t.li}>
                  {p.offSite.map((q) => (
                    <li key={q.query} className="py-0.5" style={{ borderBottom: `1px solid ${c.rule}`, color: c.ink }}>
                      “{q.query}”<Where where={q.where} />
                      {q.rivals.length > 0 && <span style={{ color: c.muted }}>, named: <span style={{ color: c.bad }}>{q.rivals.slice(0, 4).join(', ')}</span></span>}
                    </li>
                  ))}
                </ul>
              </Step>
            )}

            {p.noBrand > 0 && (
              <p className={t.p} style={{ color: c.muted }}>
                Not on the list: {p.noBrand} {p.noBrand === 1 ? 'answer' : 'answers'} used your pages and named no brand at all, yours or
                anyone&apos;s. AI answers those with a general explanation, so no wording on your page wins them.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
